import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const themes = {
  dark: {
    name: 'dark',
    background: {
      primary: '#0a0a0a',
      secondary: '#1a1a1a',
      tertiary: '#2a2a2a',
      card: '#1c1c1c',
      gradient: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)'
    },
    text: {
      primary: '#ffffff',
      secondary: '#a0aec0',
      muted: '#718096',
      accent: '#8B5CF6'
    },
    border: {
      primary: '#374151',
      secondary: '#4b5563',
      accent: '#8B5CF6'
    },
    button: {
      primary: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      secondary: '#374151',
      hover: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)'
    },
    ai: {
      glow: 'rgba(139, 92, 246, 0.3)',
      accent: '#8B5CF6',
      secondary: '#3b82f6',
      gradient: 'linear-gradient(90deg, #8B5CF6 0%, #3b82f6 100%)'
    }
  },
  light: {
    name: 'light',
    background: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
      card: '#ffffff',
      gradient: 'linear-gradient(135deg, #ffffff 0%, #f5f3ff 50%, #ede9fe 100%)'
    },
    text: {
      primary: '#111827',
      secondary: '#374151',
      muted: '#6B7280',
      accent: '#7C3AED'
    },
    border: {
      primary: '#e5e7eb',
      secondary: '#d1d5db',
      accent: '#7C3AED'
    },
    button: {
      primary: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      secondary: '#e5e7eb',
      hover: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)'
    },
    ai: {
      glow: 'rgba(124, 58, 237, 0.2)',
      accent: '#7C3AED',
      secondary: '#3b82f6',
      gradient: 'linear-gradient(90deg, #7C3AED 0%, #3b82f6 100%)'
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Default to LIGHT theme for all POS surfaces (May 2026 — every iPad
    // / cashier station is expected to run in a brightly lit shop, dark
    // mode washes out at glare angles). Users can still flip via the
    // header toggle; their choice is persisted in `pos-theme`.
    const saved = localStorage.getItem('pos-theme');
    return saved === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('pos-theme', theme);
    // Update body class for global styles
    document.body.className = theme === 'light' ? 'light-theme' : 'dark-theme';
    // Also toggle 'dark' class on html element for Tailwind dark: prefix support
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Save to backend if user is logged in
    const posToken = localStorage.getItem('pos_token');
    if (posToken) {
      import('../utils/posApi').then(({ default: posApi }) => {
        posApi.post('/api/pos/user/preferences', {
          preferences: { theme }
        }).then(() => {
          console.log('✅ Theme preference saved to backend');
        }).catch(error => {
          console.error('Failed to save theme preference:', error);
        });
      });
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const currentTheme = themes[theme];

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colors: currentTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
